import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, type Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredComics = getComicsByCategory(activeSlug);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return b.updatedAt.localeCompare(a.updatedAt);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name === '全部' ? '全部分类' : currentCategory.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-2">
              {currentCategory.name === '全部' ? '全部漫画' : `${currentCategory.name}漫画`}
            </h1>
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-primary-500">{filteredComics.length}</strong> 部漫画作品
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs text-foreground-500">排序：</span>
            {[
              { key: 'latest' as const, label: '最新更新' },
              { key: 'popular' as const, label: '人气最高' },
              { key: 'rating' as const, label: '评分最高' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === s.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画作品</p>
              <Link
                to="/category/all"
                className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                浏览全部漫画
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}